// Feb 2026 · Cycle 63 — Teacher Classroom link (device-local).
// Parallel to the older `mwq_class_link` (used by the earlier
// classes/progress API). Backed by /api/teacher/* endpoints.
//
// Storage key: we_teacher_link_v1
//   { class_id, member_id, join_code, member_alias, class_name, device_id }

const KEY = "we_teacher_link_v1";
const API = process.env.REACT_APP_BACKEND_URL;

function uid() {
  try { return crypto.randomUUID?.() || `dev-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; }
  catch { return `dev-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; }
}

function _deviceId() {
  try {
    let id = localStorage.getItem("we_teacher_device_id");
    if (!id) { id = uid(); localStorage.setItem("we_teacher_device_id", id); }
    return id;
  } catch { return "anon"; }
}

export function getTeacherLink() {
  try { return JSON.parse(localStorage.getItem(KEY) || "null"); }
  catch { return null; }
}
export function saveTeacherLink(link) {
  try { localStorage.setItem(KEY, JSON.stringify(link)); }
  catch { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent("we-teacher-link-changed", { detail: link })); }
  catch { /* ignore */ }
}
export function clearTeacherLink() {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent("we-teacher-link-changed", { detail: null })); }
  catch { /* ignore */ }
}

/** Join a class using its 6-letter code. */
export async function joinTeacherClass({ joinCode, memberAlias, learnerId }) {
  if (!joinCode) throw new Error("Enter your class code");
  if (!memberAlias && !learnerId) throw new Error("Enter a first name or alias");
  const device = _deviceId();
  // Institution mode: a school-issued learner id makes the link durable across devices (school_os learner identity).
  const r = await fetch(`${API}/api/teacher/classes/join`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ join_code: joinCode.trim().toUpperCase(), member_alias: (memberAlias || "Learner").trim(), device_id: device, ...(learnerId ? { learner_id: learnerId.trim() } : {}) }),
  });
  const j = await r.json();
  if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
  const link = {
    class_id: j.class_id || j.id ? (j.class_id || j.class_id === undefined ? j.class_id : null) : null,
  };
  const record = {
    class_id: j.class_id,
    member_id: j.id,
    class_name: j.class_name,
    member_alias: j.member_alias,
    join_code: joinCode.trim().toUpperCase(),
    device_id: device,
  };
  saveTeacherLink(record);
  return record;
}

/** Unlink the device from the teacher's class. Parent-initiated. */
export async function unlinkTeacherClass() {
  const link = getTeacherLink();
  if (!link?.class_id || !link?.member_id) { clearTeacherLink(); return; }
  const device = link.device_id || _deviceId();
  let ok = false;
  try {
    const r = await fetch(`${API}/api/teacher/classes/${link.class_id}/members/${link.member_id}?device_id=${encodeURIComponent(device)}`, { method: "DELETE" });
    ok = r.ok || r.status === 404; // 404 = already gone server-side
  } catch { ok = false; }
  if (!ok) throw new Error("unlink_failed"); // keep the local link so the UI can show "try again" — never pretend consent was revoked
  clearTeacherLink();
}

/** Read the client-side mastery snapshot from localStorage and push
 *  it to the teacher's classroom. Never throws. Lightly throttled to
 *  avoid a burst of writes on multi-event sessions. */
let _lastReport = 0;
export async function reportTeacherSnapshot({ force = false } = {}) {
  const link = getTeacherLink();
  if (!link?.member_id) return { skipped: "no-link" };
  const now = Date.now();
  if (!force && (now - _lastReport) < 5 * 1000) return { skipped: "throttled" };
  _lastReport = now;

  // Read the mastery store directly to avoid a hard import cycle.
  let mastery = {};
  try {
    const raw = JSON.parse(localStorage.getItem("we_mastery_progress_v1") || "{}");
    Object.entries(raw).forEach(([k, v]) => {
      if (k.startsWith("__placement__")) return;
      mastery[k] = Math.round(Number(v?.masteryScore) || 0);
    });
  } catch { /* ignore */ }

  // Read gap snapshot (client-side helper).
  let gaps = [];
  try {
    const raw = JSON.parse(localStorage.getItem("we_gap_tracker_v1") || "{}");
    gaps = Array.isArray(raw?.gapList) ? raw.gapList.slice(0, 40).map((g) => ({ skill: g.skill, foundational: !!g.foundational })) : [];
  } catch { /* ignore */ }

  // Weekly completion + certificates from progress record.
  let certificates = 0; let activityLastAt = null;
  try {
    const p = JSON.parse(localStorage.getItem("we_progress_v1") || "{}");
    certificates = Array.isArray(p?.badges) ? p.badges.length : 0;
    activityLastAt = p?.lastPlayed ? new Date(p.lastPlayed).toISOString() : new Date().toISOString();
  } catch { /* ignore */ }

  try {
    const r = await fetch(`${API}/api/teacher/members/${link.member_id}/report`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        device_id: link.device_id || _deviceId(),
        mastery,
        gaps,
        activity_last_at: activityLastAt,
        certificates,
      }),
    });
    if (!r.ok) return { ok: false, status: r.status };
    return { ok: true };
  } catch (e) {
    return { ok: false, error: String(e?.message || e) };
  }
}

// Auto-attach: report mastery snapshot on mastery attempts, throttled.
// `we-gaps-changed` is a state-transition event and bypasses throttle
// (we want the teacher's cohort view to reflect it immediately).
try {
  if (typeof window !== "undefined") {
    window.addEventListener("we-mastery-attempt", () => { reportTeacherSnapshot(); });
    window.addEventListener("we-gaps-changed", () => { reportTeacherSnapshot({ force: true }); });
  }
} catch { /* ignore */ }
